/**
 * EventLogger Server Adapter
 * Posts server-side analytics events to the Bluestone eventLogger service.
 * Mirrors legacy `eventLogger()` from old-react.
 */

import { warnOnce } from './logger';
import { getCommonServerAttributes } from '../../utils/commonAttributes';
import { resolveEventLoggerBaseUrl, toFormBody } from '../../utils/eventLoggerHttp';
import type { EventTracker } from '../../domain/interfaces/EventTracker';

export interface EventLoggerConfig {
  baseUrl?: string;
  enabled?: boolean;
}

export class EventLoggerAdapter implements EventTracker {
  private readonly config: EventLoggerConfig;
  private initialized = false;

  constructor(config: EventLoggerConfig = {}) {
    this.config = {
      enabled: true,
      ...config,
      // Explicit `undefined` from build config must not wipe the env fallback.
      baseUrl: config.baseUrl?.trim() || resolveEventLoggerBaseUrl() || undefined,
    };
  }

  async initialize(): Promise<void> {
    if (!this.config.enabled) {
      return;
    }
    if (!this.config.baseUrl) {
      warnOnce(
        'eventlogger:no-base-url',
        'EventLogger base URL is not configured (set VITE_EVENT_LOGGER_URL or VITE_PAGE_API_BASE_URL)',
      );
      return;
    }
    this.initialized = true;
  }

  trackEvent(_eventName: string, _properties?: Record<string, unknown>): void {
    // Server events are sent via logEventLogger with `_eventId` payloads.
  }

  logEventLogger(payload: Record<string, unknown>): void {
    if (!this.config.enabled) {
      return;
    }
    if (!this.initialized || !this.config.baseUrl) {
      warnOnce(
        'eventlogger:not-initialized',
        'EventLogger adapter is not initialized; server events are dropped',
      );
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    if (!payload._eventId) {
      warnOnce('eventlogger:missing-event-id', 'EventLogger payload missing `_eventId`');
      return;
    }

    const mergedPayload = {
      ...getCommonServerAttributes(),
      ...payload,
    };

    const url = `https://event.bluestone.com/eventlogger/server/log`;
    const body = toFormBody(mergedPayload);

    void fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body,
      credentials: 'include',
    }).catch(() => {
      // Fire-and-forget — same as legacy eventLogger.
    });
  }
}
